// UnitRadio.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useRadio } from '../../contexts/RadioContext';
import './UnitRadio.css';

/**
 * Rewritten UnitRadio
 * - No implicit navigation or history changes (prevents accidental router warnings)
 * - Robust subscriptions (auto-unsub on channel change / unmount)
 * - Solid keyboard/touch PTT with global “release” handlers (prevents stuck TX)
 * - Spacebar handling uses capture + preventDefault to avoid scrolling or router focus traps
 * - Guarded effects + stable refs to avoid stale closures
 */

const UnitRadio: React.FC = () => {
  const {
    user,
    talkgroups,
    activeTalkgroups,
    isTransmitting,
    transmissionStartTime,
    devices,
    selectedInputDevice,
    selectedOutputDevice,
    isInitialized,
    startTransmitting,
    stopTransmitting,
    subscribeToTalkgroup,
    unsubscribeFromTalkgroup,
    updateDevice,
    testAudioPlayback,
    isConnected,
    txFxEnabled,
    setTxFxEnabled,
    p25VocoderConfig,
    setP25VocoderMode,
    currentUserChannel,
    moveUserToTalkgroup,
    getUnitStatusColor,
  } = useRadio();

  // ---------------------------
  // Local UI State
  // ---------------------------
  const [isPTTPressed, setIsPTTPressed] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [, forceTick] = useState(0); // for lightweight timers/indicators
  const [showSettings, setShowSettings] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // ---------------------------
  // Refs
  // ---------------------------
  const mountedRef = useRef(true);
  const pttButtonRef = useRef<HTMLButtonElement>(null);
  const txTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tickerRef = useRef<NodeJS.Timeout | null>(null);
  const currentSubRef = useRef<string | null>(null);
  const subscribeRef = useRef(subscribeToTalkgroup);
  const unsubscribeRef = useRef(unsubscribeFromTalkgroup);

  // keep latest fn refs
  useEffect(() => { subscribeRef.current = subscribeToTalkgroup; }, [subscribeToTalkgroup]);
  useEffect(() => { unsubscribeRef.current = unsubscribeFromTalkgroup; }, [unsubscribeFromTalkgroup]);

  // ---------------------------
  // Derived: assigned talkgroup (based on current active channel)
  // ---------------------------
  const assignedTalkgroup = useMemo(() => {
    // Use the current active channel set by the dispatcher
    if (currentUserChannel) {
      return talkgroups.find(tg => tg.id === currentUserChannel) || null;
    }
    return null;
  }, [currentUserChannel, talkgroups]);

  // ---------------------------
  // Subscribe/Unsubscribe lifecycle
  // ---------------------------
  useEffect(() => {
    if (!isInitialized || !assignedTalkgroup) return;

    const tgId = assignedTalkgroup.id;
    if (currentSubRef.current === tgId) return;

    // Unsubscribe previous
    if (currentSubRef.current && currentSubRef.current !== tgId) {
      unsubscribeRef.current(currentSubRef.current);
    }

    // Subscribe new
    subscribeRef.current(tgId);
    currentSubRef.current = tgId;
  }, [isInitialized, assignedTalkgroup?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (currentSubRef.current) {
        unsubscribeRef.current(currentSubRef.current);
        currentSubRef.current = null;
      }
      if (txTimerRef.current) clearInterval(txTimerRef.current);
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, []);

  // ---------------------------
  // Lightweight UI ticker for lamps/meters
  // ---------------------------
  useEffect(() => {
    tickerRef.current && clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      forceTick(t => (t + 1) % 1000000);
    }, 120);
    return () => {
      if (tickerRef.current) clearInterval(tickerRef.current);
      tickerRef.current = null;
    };
  }, []);

  // Update current time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // ---------------------------
  // TX timer
  // ---------------------------
  useEffect(() => {
    if (txTimerRef.current) {
      clearInterval(txTimerRef.current);
      txTimerRef.current = null;
    }
    if (isTransmitting && transmissionStartTime) {
      txTimerRef.current = setInterval(() => {
        if (!mountedRef.current) return;
        forceTick(t => (t + 1) % 1000000);
      }, 1000);
    }
    return () => {
      if (txTimerRef.current) clearInterval(txTimerRef.current);
      txTimerRef.current = null;
    };
  }, [isTransmitting, transmissionStartTime]);

  // ---------------------------
  // Helpers
  // ---------------------------
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getTxDuration = () => {
    if (!transmissionStartTime) return 0;
    return Math.floor((Date.now() - transmissionStartTime) / 1000);
  };

  const getSignalStrength = () => {
    if (!isConnected) return 'none';
    // Cosmetic; could be driven by real metrics later
    if (volume >= 80) return 'excellent';
    if (volume >= 60) return 'good';
    if (volume >= 40) return 'fair';
    if (volume >= 20) return 'poor';
    return 'none';
  };

  const isChannelBusy = useCallback(() => {
    if (!assignedTalkgroup) return false;
    const speakers = activeTalkgroups.get(assignedTalkgroup.id);
    return !!(speakers && speakers.length > 0);
  }, [assignedTalkgroup, activeTalkgroups]);

  const rxActive = useMemo(() => {
    if (!assignedTalkgroup) return false;
    return isChannelBusy();
  }, [assignedTalkgroup?.id, isChannelBusy]);

  // ---------------------------
  // PTT Handlers (mouse/touch)
  // ---------------------------
  const handlePTTPress = useCallback(() => {
    if (!assignedTalkgroup || isTransmitting || isPTTPressed) return;

    // Units cannot transmit on busy channels
    if (isChannelBusy()) {
      console.log('[UnitRadio] PTT blocked - channel is busy');
      return;
    }

    // prime audio contexts (prevents autoplay issues)
    testAudioPlayback();

    setIsPTTPressed(true);
    startTransmitting(assignedTalkgroup.id);
  }, [assignedTalkgroup, isTransmitting, isPTTPressed, isChannelBusy, startTransmitting, testAudioPlayback]);

  const handlePTTRelease = useCallback(() => {
    if (!isPTTPressed) return;
    setIsPTTPressed(false);
    stopTransmitting();
  }, [isPTTPressed, stopTransmitting]);

  // Global release guards (avoid stuck TX if pointer leaves button / route changes)
  useEffect(() => {
    const up = () => handlePTTRelease();
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up, { passive: false });
    window.addEventListener('touchcancel', up, { passive: false });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', up);
    };
  }, [handlePTTRelease]);

  // ---------------------------
  // Keyboard PTT (SPACE)
  // Capture & preventDefault so Space doesn’t scroll or trigger router focus traps.
  // ---------------------------
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      // Prevent default scrolling / button “clicking” that can cascade into router warnings
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      if (!isTransmitting && !isPTTPressed) handlePTTPress();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      e.preventDefault();
      e.stopPropagation();
      if (isPTTPressed) handlePTTRelease();
    };

    // use capture to intercept before other handlers (including router-level handlers)
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keyup', onKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true } as any);
      window.removeEventListener('keyup', onKeyUp, { capture: true } as any);
    };
  }, [handlePTTPress, handlePTTRelease, isTransmitting, isPTTPressed]);

  // ---------------------------
  // UI Handlers
  // ---------------------------

  const handleVolumeChange = (v: number) => setVolume(v);
  const toggleMute = () => setIsMuted(m => !m);

  // ---------------------------
  // Early exits
  // ---------------------------
  if (!user) {
    return (
      <div className="unit-radio">
        <div className="loading">Connecting to radio system...</div>
      </div>
    );
  }

  if (!assignedTalkgroup) {
    return (
      <div className="unit-radio">
        <div className="error">No talkgroup assigned</div>
      </div>
    );
  }

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <div className="unit-radio" role="application" aria-label="Unit Radio">
      {/* Header */}
      <div className="radio-header">
        <div className="talkgroup-info">
          <div className="talkgroup-name" aria-live="polite">{assignedTalkgroup?.name || 'NO CHANNEL'}</div>
          <div className="talkgroup-id">Channel: {assignedTalkgroup?.id || 'N/A'}</div>
        </div>

        <div className="call-sign">
          <div className="unit-name">{user.displayName}</div>
          <div className="unit-id">
            Unit: {user.claimedUnit || user.id}
            {user.unitStatus && (
              <span 
                className="unit-status-indicator"
                style={{
                  backgroundColor: getUnitStatusColor(user.unitStatus),
                  marginLeft: '8px',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '10px',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                {user.unitStatus}
              </span>
            )}
          </div>
        </div>

        <div className="signal-status" aria-label="Signal status">
          <div className={`signal-indicator ${getSignalStrength()}`}>
            <div className="signal-bars" aria-hidden>
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
            </div>
            <span className="signal-text">{isConnected ? 'CONNECTED' : 'OFFLINE'}</span>
          </div>
        </div>

        <div className="time-status">
          <div className="utc-time">
            <span>UTC {currentTime.toISOString().substr(11, 8)}</span>
          </div>
        </div>

        <div className="header-actions">
          <button
            className="settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Settings"
            aria-label="Open settings menu"
          >
            <Icon icon="material-symbols:settings" width="22" height="22" />
          </button>
        </div>
      </div>

      {/* Settings Popup */}
      {showSettings && (
        <div className="settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-popup" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h3>Settings</h3>
              <button className="close-btn" onClick={() => setShowSettings(false)}><Icon icon="material-symbols:close" width="20" height="20" /></button>
            </div>
            <div className="settings-content">
              <div className="settings-section">
                <h4>Audio Devices</h4>
                <div className="device-controls">
                  <label htmlFor="input-device">Input Device:</label>
                  <select
                    id="input-device"
                    value={selectedInputDevice || ''}
                    onChange={(e) => updateDevice('input', e.target.value)}
                    className="device-select"
                    title="Input Device"
                    aria-label="Select input device"
                  >
                    {(devices.filter(d => d.kind === 'audioinput').length
                      ? devices.filter(d => d.kind === 'audioinput')
                      : [{ deviceId: '', label: 'No inputs', kind: 'audioinput' as const }]
                    ).map((d: any) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                    ))}
                  </select>

                  <label htmlFor="output-device">Output Device:</label>
                  <select
                    id="output-device"
                    value={selectedOutputDevice || ''}
                    onChange={(e) => updateDevice('output', e.target.value)}
                    className="device-select"
                    title="Output Device"
                    aria-label="Select output device"
                  >
                    {(devices.filter(d => d.kind === 'audiooutput').length
                      ? devices.filter(d => d.kind === 'audiooutput')
                      : [{ deviceId: '', label: 'No outputs', kind: 'audiooutput' as const }]
                    ).map((d: any) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="settings-section">
                <h4>P25 Vocoder</h4>
                <div className="fx-controls">
                  <div className="fx-toggle">
                    <label className="fx-switch">
                      <input
                        type="checkbox"
                        checked={txFxEnabled}
                        onChange={(e) => setTxFxEnabled(e.target.checked)}
                        aria-label="Radio FX (TX vocoder)"
                      />
                      <span className="slider" />
                    </label>
                    <span className="fx-label">{txFxEnabled ? 'P25 Vocoder: ON' : 'P25 Vocoder: OFF'}</span>
                  </div>
                  
                  {txFxEnabled && (
                    <div className="p25-mode-selector">
                      <label htmlFor="p25-mode" className="p25-label">P25 Mode:</label>
                      <select
                        id="p25-mode"
                        value={p25VocoderConfig.mode}
                        onChange={(e) => setP25VocoderMode(e.target.value as 'P25_PHASE1' | 'P25_PHASE2' | 'CUSTOM')}
                        className="p25-select"
                      >
                        <option value="P25_PHASE1">Phase 1 (IMBE)</option>
                        <option value="P25_PHASE2">Phase 2 (AMBE+2)</option>
                        <option value="CUSTOM">Custom</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="radio-main">
        {/* Channel Busy */}
        <div className={`channel-busy-lamp ${isChannelBusy() ? 'active' : ''}`} aria-live="polite">
          <div className="busy-light" />
          <span>{isChannelBusy() ? 'CHANNEL BUSY' : 'CHANNEL CLEAR'}</span>
        </div>

        {/* RX/TX lights */}
        <div className="status-lights" aria-label="RX TX Status">
          <div className="status-light-container">
            <div className="status-label">RX</div>
            <div
              className={`status-light ${rxActive ? 'active' : 'inactive'}`}
              title={`Receiving: ${rxActive ? 'Yes' : 'No'} (${assignedTalkgroup?.id || 'N/A'})`}
              aria-live="polite"
            />
          </div>
          <div className="status-light-container">
            <div className="status-label">TX</div>
            <div
              className={`status-light ${isTransmitting ? 'active' : 'inactive'}`}
              title={`Transmitting: ${isTransmitting ? 'Yes' : 'No'}`}
              aria-live="polite"
            />
          </div>
        </div>

        {/* PTT */}
        <div className="ptt-section">
          <button
            ref={pttButtonRef}
            type="button"
            className={`ptt-button ${isTransmitting ? 'transmitting' : ''} ${isPTTPressed ? 'pressed' : ''} ${isChannelBusy() && !isTransmitting ? 'busy' : ''}`}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handlePTTPress(); }}
            onMouseUp={(e) => { e.preventDefault(); e.stopPropagation(); handlePTTRelease(); }}
            onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); handlePTTPress(); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handlePTTRelease(); }}
            aria-pressed={isTransmitting}
            aria-label="Push To Talk"
          >
            <div className="ptt-icon" aria-hidden>🎤</div>
            <div className="ptt-label">PUSH TO TALK</div>
          </button>
        </div>

        {/* TX Timer */}
        {isTransmitting && (
          <div className="transmission-timer" aria-live="polite">
            <div className="timer-display">{formatTime(getTxDuration())}</div>
            <div className="timer-label">TX TIME</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="radio-footer">
        {/* Channel Selector */}
        <div className="channel-selector">
          <label htmlFor="channelSelect">Channel:</label>
          <select
            id="channelSelect"
            value={currentUserChannel || ''}
            onChange={async (e) => {
              if (e.target.value && user?.sub) {
                try {
                  await moveUserToTalkgroup(user.sub, e.target.value);
                } catch (error) {
                  console.error('Failed to change channel:', error);
                }
              }
            }}
            className="channel-select"
          >
            <option value="">Select Channel</option>
            {talkgroups.map((tg: any) => (
              <option key={tg.id} value={tg.id}>
                {tg.name} ({tg.id})
              </option>
            ))}
          </select>
        </div>

        {/* Volume */}
        <div className="volume-control">
          <label htmlFor="volumeSlider">Volume</label>
          <input
            id="volumeSlider"
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => handleVolumeChange(parseInt(e.target.value, 10))}
            className="volume-slider"
          />
          <span className="volume-value">{volume}%</span>
        </div>

        {/* Mute */}
        <div className="mute-control">
          <button
            type="button"
            className={`mute-button ${isMuted ? 'muted' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMute(); }}
            title="Mute RX (Receive)"
            aria-pressed={isMuted}
            aria-label={isMuted ? 'Unmute receive audio' : 'Mute receive audio'}
          >
            {isMuted ? <Icon icon="material-symbols:volume-off" width="20" height="20" /> : <Icon icon="material-symbols:volume-up" width="20" height="20" />}
          </button>
        </div>

      </div>
    </div>
  );
};

export default UnitRadio;
